import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
import type { ExecutionTimelineModel, ExecutionTimelinePoint } from './executionTimelineModel';
import type { ExecutionActivityViewModel, ExecutionTripViewModel } from './executionTypes';

interface ExecutionTimelineComparisonStripProps {
  trip: ExecutionTripViewModel;
  model: ExecutionTimelineModel;
  activeActivityId: string | null;
  hoveredActivityId: string | null;
  onFocusActivity: (activity: ExecutionActivityViewModel) => void;
  onHoverActivity: (activityId: string | null) => void;
}

const AXIS_HEIGHT = 34;
const ROW_HEIGHT = 60;
const PLANNED_Y = AXIS_HEIGHT + 27;
const ACTUAL_Y = AXIS_HEIGHT + ROW_HEIGHT + 27;
const MARKER_SIZE = 22;

const formatTime = (epoch: number, timezone: string): string => new Intl.DateTimeFormat('en-IN', {
  timeZone: timezone,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
}).format(new Date(epoch));

const formatOptionalTime = (epoch: number | null, timezone: string): string => epoch == null ? '—' : formatTime(epoch, timezone);

const deltaLabel = (activity: ExecutionActivityViewModel): string | null => {
  if (activity.arrivalDeltaMinutes == null) return null;
  return `${activity.arrivalDeltaMinutes > 0 ? '+' : ''}${activity.arrivalDeltaMinutes}m`;
};

const pointTooltip = (point: ExecutionTimelinePoint, lane: 'planned' | 'actual', timezone: string): string => {
  const activity = point.activity;
  if (lane === 'planned') {
    return `${activity.title} · Planned #${activity.plannedSequence ?? '—'} · ${formatOptionalTime(activity.plannedArrivalAtEpoch, timezone)}–${formatOptionalTime(activity.plannedDepartureAtEpoch, timezone)}`;
  }
  return `${activity.title} · Actual #${activity.actualSequence ?? '—'} / Planned #${activity.plannedSequence ?? 'unplanned'} · In ${formatOptionalTime(activity.actualCheckInAtEpoch, timezone)} · Out ${formatOptionalTime(activity.actualCheckOutAtEpoch, timezone)} · Dwell ${activity.dwellMinutes ?? '—'}m${deltaLabel(activity) ? ` · ${deltaLabel(activity)}` : ''}`;
};

const ExecutionTimelineComparisonStrip: React.FC<ExecutionTimelineComparisonStripProps> = ({
  trip,
  model,
  activeActivityId,
  hoveredActivityId,
  onFocusActivity,
  onHoverActivity,
}) => {
  const theme = useTheme();
  const timezone = trip.plannedTrip.timezone;
  const dark = theme.palette.mode === 'dark';
  const guideColor = dark ? 'rgba(148,163,184,0.28)' : 'rgba(15,23,42,0.18)';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '126px minmax(0, 1fr)', minHeight: 166 }}>
      <Box sx={{ pt: `${AXIS_HEIGHT}px`, pr: 0.8 }}>
        <Box sx={{ height: ROW_HEIGHT, px: 1, py: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: alpha('#64748b', dark ? 0.1 : 0.045) }}>
          <Typography sx={{ fontSize: 10, fontWeight: 850, letterSpacing: 0.5, color: 'text.secondary' }}>PLANNED</Typography>
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.2 }}>{trip.activities.filter((activity) => activity.plannedSequence != null).length} activities</Typography>
        </Box>
        <Box sx={{ height: ROW_HEIGHT, mt: 0.35, px: 1, py: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: alpha(trip.color, 0.25), bgcolor: alpha(trip.color, dark ? 0.12 : 0.055), position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', left: 0, insetBlock: 0, width: 3, bgcolor: trip.color } }}>
          <Typography sx={{ fontSize: 10, fontWeight: 850, letterSpacing: 0.5, color: trip.color }}>ACTUAL</Typography>
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.2 }}>{trip.metrics.terminalCount}/{trip.metrics.totalDeliveryCount} complete</Typography>
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: 7 }, '&::-webkit-scrollbar-thumb': { borderRadius: 999, bgcolor: 'rgba(148,163,184,0.55)' } }} data-timeline-no-drag="true">
        <Box sx={{ width: model.contentWidth, minWidth: model.contentWidth, height: 166, position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0 }}>
            {model.ticks.map((tick) => (
              <React.Fragment key={tick.epoch}>
                <Box sx={{ position: 'absolute', left: `${tick.percent}%`, top: 26, bottom: 4, borderLeft: '1.5px dotted', borderColor: guideColor }} />
                <Typography sx={{ position: 'absolute', left: `${tick.percent}%`, top: 4, transform: tick.percent > 96 ? 'translateX(-100%)' : tick.percent < 4 ? 'none' : 'translateX(-50%)', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.25, color: 'text.primary', fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace', whiteSpace: 'nowrap' }}>
                  {formatTime(tick.epoch, timezone)}
                </Typography>
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ position: 'absolute', left: 0, right: 0, top: PLANNED_Y, height: 1, bgcolor: guideColor }} />
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: ACTUAL_Y, height: 2, bgcolor: alpha(trip.color, 0.22) }} />

          <svg aria-hidden="true" width={model.contentWidth} height="166" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
            {model.points.filter((point) => point.plannedPercent != null && point.actualPercent != null && point.activity.kind !== 'UNPLANNED_STOP').map((point) => (
              <line
                key={point.activity.id}
                x1={`${point.plannedPercent}%`}
                y1={PLANNED_Y}
                x2={`${point.actualPercent}%`}
                y2={ACTUAL_Y}
                stroke={point.activity.arrivalDeltaMinutes && point.activity.arrivalDeltaMinutes > 15 ? '#f59e0b' : '#94a3b8'}
                strokeWidth="1.2"
                strokeDasharray="3 3"
                opacity="0.65"
              />
            ))}
          </svg>

          {model.points.map((point) => {
            const { activity } = point;
            const highlighted = activity.id === activeActivityId || activity.id === hoveredActivityId;
            const markerColor = activity.outcome === 'RTO' ? '#dc2626' : activity.kind === 'UNPLANNED_STOP' ? '#7c3aed' : activity.outcome === 'IN_PROGRESS' ? '#0f766e' : trip.color;
            return (
              <React.Fragment key={activity.id}>
                {point.plannedServiceStartPercent != null ? (
                  <Tooltip title={`Planned service ${formatOptionalTime(activity.plannedArrivalAtEpoch, timezone)}–${formatOptionalTime(activity.plannedDepartureAtEpoch, timezone)}`} arrow>
                    <Box sx={{ position: 'absolute', left: `${point.plannedServiceStartPercent}%`, top: PLANNED_Y - 3, width: `${point.plannedServiceWidthPercent}%`, minWidth: 4, height: 6, borderRadius: 999, backgroundImage: 'linear-gradient(90deg, #64748b 60%, transparent 0%)', backgroundSize: '8px 100%', opacity: 0.72 }} />
                  </Tooltip>
                ) : null}
                {point.actualTravelStartPercent != null && point.actualTravelWidthPercent > 0 ? (
                  <Tooltip title={`Travel ${activity.incomingTravel.actualMinutes ?? '—'}m · ${activity.incomingTravel.actualDistanceKm ?? '—'} km`} arrow>
                    <Box sx={{ position: 'absolute', left: `${point.actualTravelStartPercent}%`, top: ACTUAL_Y - 2, width: `${point.actualTravelWidthPercent}%`, minWidth: 4, height: 4, borderRadius: 999, bgcolor: '#2563eb', opacity: 0.72 }} />
                  </Tooltip>
                ) : null}
                {point.actualServiceStartPercent != null ? (
                  <Tooltip title={`Service/dwell ${activity.dwellMinutes ?? '—'}m · Check-out ${formatOptionalTime(activity.actualCheckOutAtEpoch, timezone)}`} arrow>
                    <Box sx={{ position: 'absolute', left: `${point.actualServiceStartPercent}%`, top: ACTUAL_Y - 4, width: `${point.actualServiceWidthPercent}%`, minWidth: 5, height: 8, borderRadius: 999, bgcolor: markerColor, opacity: 0.9 }} />
                  </Tooltip>
                ) : null}

                {point.plannedPercent != null ? (
                  <Tooltip title={pointTooltip(point, 'planned', timezone)} arrow>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Focus planned ${activity.title}`}
                      onClick={() => onFocusActivity(activity)}
                      onMouseEnter={() => onHoverActivity(activity.id)}
                      onMouseLeave={() => onHoverActivity(null)}
                      sx={{ position: 'absolute', left: `${point.plannedPercent}%`, top: PLANNED_Y, transform: 'translate(-50%, -50%)', width: MARKER_SIZE, height: MARKER_SIZE, borderRadius: '50%', border: '2px solid #64748b', bgcolor: 'background.paper', color: '#475569', display: 'grid', placeItems: 'center', p: 0, fontSize: 9.5, fontWeight: 900, fontFamily: 'monospace', cursor: 'pointer', zIndex: 3, boxShadow: highlighted ? '0 0 0 4px rgba(37,99,235,0.2)' : '0 0 0 3px rgba(100,116,139,0.12)' }}
                    >
                      {activity.plannedSequence ?? '—'}
                    </Box>
                  </Tooltip>
                ) : null}

                {point.actualPercent != null ? (
                  <Tooltip title={pointTooltip(point, 'actual', timezone)} arrow>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Focus actual ${activity.title}`}
                      onClick={() => onFocusActivity(activity)}
                      onMouseEnter={() => onHoverActivity(activity.id)}
                      onMouseLeave={() => onHoverActivity(null)}
                      sx={{ position: 'absolute', left: `${point.actualPercent}%`, top: ACTUAL_Y, transform: activity.kind === 'UNPLANNED_STOP' ? 'translate(-50%, -50%) rotate(45deg)' : 'translate(-50%, -50%)', width: MARKER_SIZE, height: MARKER_SIZE, borderRadius: activity.kind === 'UNPLANNED_STOP' ? 0.8 : '50%', border: `2px solid ${dark ? '#0f172a' : '#fff'}`, bgcolor: markerColor, color: '#fff', display: 'grid', placeItems: 'center', p: 0, fontSize: 9.5, fontWeight: 900, fontFamily: 'monospace', cursor: 'pointer', zIndex: 4, boxShadow: highlighted ? `0 0 0 5px ${alpha(markerColor, 0.25)}` : `0 0 0 3px ${alpha(markerColor, 0.15)}` }}
                    >
                      <Box component="span" sx={{ transform: activity.kind === 'UNPLANNED_STOP' ? 'rotate(-45deg)' : 'none' }}>{activity.plannedSequence ?? '!'}</Box>
                    </Box>
                  </Tooltip>
                ) : point.next && point.plannedPercent != null ? (
                  <Tooltip title={`Next stop · Planned #${activity.plannedSequence} · ${activity.title}`} arrow>
                    <Box sx={{ position: 'absolute', left: `${point.plannedPercent}%`, top: ACTUAL_Y, transform: 'translate(-50%, -50%)', width: MARKER_SIZE, height: MARKER_SIZE, borderRadius: '50%', border: '2px dashed #d97706', bgcolor: dark ? '#172033' : '#fff', color: '#d97706', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 900, zIndex: 3, boxShadow: '0 0 0 4px rgba(217,119,6,0.14)' }}>{activity.plannedSequence}</Box>
                  </Tooltip>
                ) : null}

                {point.actualPercent != null && deltaLabel(activity) ? (
                  <Typography sx={{ position: 'absolute', left: `${point.actualPercent}%`, top: ACTUAL_Y + 14, transform: 'translateX(-50%)', color: activity.arrivalDeltaMinutes && activity.arrivalDeltaMinutes > 15 ? 'warning.dark' : 'text.secondary', fontSize: 8.5, fontWeight: 800, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{deltaLabel(activity)}</Typography>
                ) : null}
              </React.Fragment>
            );
          })}

          {model.currentPercent != null ? (
            <Box sx={{ position: 'absolute', left: `${model.currentPercent}%`, top: 27, bottom: 2, borderLeft: '2px solid #0f766e', zIndex: 2 }}>
              <Typography sx={{ position: 'absolute', top: -1, left: 4, color: '#0f766e', fontSize: 8.5, fontWeight: 900, whiteSpace: 'nowrap' }}>NOW</Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default ExecutionTimelineComparisonStrip;
